import { Link } from "react-router-dom";
import type { ChangeItem } from "@akhshy/types";
import { SectionView } from "../components/templates/SectionView.tsx";
import { Spinner, LoadError } from "../components/atoms/Spinner.tsx";
import { LoadMore } from "../components/atoms/LoadMore.tsx";
import { usePaginated } from "../lib/usePaginated.ts";
import { client } from "../lib/api.ts";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

function itemPath(i: ChangeItem): string {
  switch (i.entryType) {
    case "vault":
      return `/vault/${i.slug ?? i.entryId}`;
    case "prompt":
      return `/prompts/${i.slug ?? i.entryId}`;
    case "ritual":
      return "/ritual";
    case "project":
      return "/projects";
  }
}

function displayDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function WhatsNewPage() {
  const { items, loading, error, hasMore, total, loadMore } = usePaginated<ChangeItem>(
    (page) => client.whatsNew({ page, pageSize: 20 }),
  );

  return (
    <SectionView
      ariaLabel="What's new"
      title="What's new"
      sub="Everything recently added or updated across the desk — no redeploys here, content changes live. Subscribe with your feed reader if that's your thing."
    >
      <p className="whats-new-rss">
        <a href={`${API_URL}/whats-new/feed.xml`} target="_blank" rel="noopener">
          RSS feed →
        </a>
      </p>
      {loading && items.length === 0 && <Spinner label="checking the changelog" />}
      {error && items.length === 0 && (
        <LoadError label="couldn't load the changes right now" />
      )}
      {!loading && !error && items.length === 0 && (
        <p className="load-note">nothing new yet — the desk is quiet.</p>
      )}
      <ul className="whats-new-list">
        {items.map((i) => (
          <li key={`${i.entryType}:${i.entryId}`} className="whats-new-item">
            <span className={`whats-new-change whats-new-change--${i.change}`}>
              {i.change}
            </span>
            <span className={`entry-badge entry-badge--${i.entryType}`}>{i.entryType}</span>
            <Link to={itemPath(i)}>{i.title}</Link>
            <span className="whats-new-date">{displayDate(i.updatedAt)}</span>
            {i.description && <span className="whats-new-desc">{i.description}</span>}
          </li>
        ))}
      </ul>
      {items.length > 0 && (
        <LoadMore
          hasMore={hasMore}
          loading={loading}
          total={total}
          count={items.length}
          onClick={loadMore}
        />
      )}
    </SectionView>
  );
}
